import type { FC } from "react";
import type { Recipient } from "../../../../hooks/use-email-compose";

export type ReplyConfirmModalProps = {
  isOpen: boolean;
  subject: string;
  body: string;
  recipients: Recipient[];
  fromName: string;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * 返信メール送信確認モーダル
 * @param {ReplyConfirmModalProps} props
 * @return {JSX.Element}
 */
export const ReplyConfirmModal: FC<ReplyConfirmModalProps> = ({
  isOpen,
  subject,
  body,
  recipients,
  fromName,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  const toRecipients = recipients.filter(r => r.type === "to");
  const ccRecipients = recipients.filter(r => r.type === "cc");
  const bccRecipients = recipients.filter(r => r.type === "bcc");

  /**
   * 宛先をフォーマットする
   * @param {Recipient} r - 宛先
   * @return {string} フォーマット済み文字列
   */
  const formatRecipient = (r: Recipient): string =>
    r.name ? `${r.name} <${r.email}>` : r.email;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-lg">
        <h3 className="font-bold text-lg mb-4">メール送信の確認</h3>

        <div className="space-y-3">
          {/* 送信者 */}
          <div>
            <span className="text-xs text-gray-500">送信者</span>
            <p className="text-sm">{fromName}</p>
          </div>

          {/* 宛先 (To) */}
          <div>
            <span className="text-xs text-gray-500">宛先 (To)</span>
            {toRecipients.map(r => (
              <p key={r.email} className="text-sm">
                {formatRecipient(r)}
              </p>
            ))}
          </div>

          {/* CC */}
          {ccRecipients.length > 0 && (
            <div>
              <span className="text-xs text-gray-500">CC</span>
              {ccRecipients.map(r => (
                <p key={r.email} className="text-sm">
                  {formatRecipient(r)}
                </p>
              ))}
            </div>
          )}

          {/* BCC */}
          {bccRecipients.length > 0 && (
            <div>
              <span className="text-xs text-gray-500">BCC</span>
              {bccRecipients.map(r => (
                <p key={r.email} className="text-sm">
                  {formatRecipient(r)}
                </p>
              ))}
            </div>
          )}

          {/* 件名 */}
          <div>
            <span className="text-xs text-gray-500">件名</span>
            <p className="text-sm font-semibold">{subject}</p>
          </div>

          {/* 本文プレビュー */}
          <div>
            <span className="text-xs text-gray-500">本文</span>
            <div className="bg-gray-50 rounded-lg p-3 mt-1 max-h-60 overflow-y-auto">
              <p className="text-sm whitespace-pre-wrap">{body}</p>
            </div>
          </div>

          {/* 開発環境の警告 */}
          <div className="alert alert-warning text-sm py-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="stroke-current shrink-0 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              role="img"
              aria-label="警告"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            <span>
              開発環境のため、実際の送信先は yuta.shimogishi@techreis.co.jp
              に変更されます
            </span>
          </div>
        </div>

        <div className="modal-action">
          <button className="btn btn-ghost" onClick={onCancel} type="button">
            キャンセル
          </button>
          <button className="btn btn-primary" onClick={onConfirm} type="button">
            送信する
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={onCancel} />
    </div>
  );
};
